import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { Fragment } from 'react';
import type { ComponentProps } from 'react';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { REMAINING_STOCK_LIMIT } from 'Components/utils/accommodation/accommodation.utils';
import { AccommodationRoom } from 'Components/widgets/accommodation-room/AccommodationRoom';
import { FlexRates } from 'Components/widgets/flex-rates/FlexRates';
import { AccommodationHeader } from './header/AccommodationHeader';
import { AccommodationSeparator } from './separator/AccommodationSeparator';
import { themes } from './Accommodation.themes';
export type Props = AccommodationEntity & {
  accommodationsComparator?: React.ReactNode;
  className?: string;
  hasHiddenStock?: boolean;
  isBooking?: boolean;
  isInMoreRoomLayer?: boolean;
  isUpgradableOnline?: boolean;
  isLoading?: boolean;
  proposal?: Proposal | null | undefined;
  selectedRate?: string;
  setSelectedRate?: (arg0: string) => void;
  switchOffers?: (offers: Array<string>) => void;
  theme?: keyof typeof themes;
};
export const Accommodation = ({
  accommodationsComparator,
  className,
  key,
  hasFlexRates = false,
  hasHiddenStock = false,
  isBooking = false,
  isCabin = false,
  isInMoreRoomLayer,
  isLoading = false,
  proposal,
  rooms = [],
  selectedRate,
  setSelectedRate,
  stock,
  switchOffers,
  theme = 'default',
}: Props) => {
  const { thFlexRates, thRoom, thSeparated } = themes[theme];
  const { isTablet } = useResponsive();
  const isMobile = !isTablet;
  const counters = isCabin
    ? null
    : rooms.reduce(
        (t, r) => ({
          adults: t.adults + (r.distribution?.adults?.length || 0),
          babies: t.babies + (r.distribution?.babies?.length || 0),
          children: t.children + (r.distribution?.children?.length || 0),
          rooms: t.rooms + r.quantity,
        }),
        {
          adults: 0,
          babies: 0,
          children: 0,
          rooms: 0,
        },
      );
  const firstRoomDates = (rooms[0]?.dates ?? {}) as { startDate?: number; endDate?: number };
  const hasConsecutiveRooms = rooms.some(
    ({ dates }) =>
      dates.startDate !== firstRoomDates.startDate || dates.endDate !== firstRoomDates.endDate,
  );
  return (
    <div className={className} data-testid="Accommodation" key={key}>
      {counters && counters.rooms > 1 && (
        <AccommodationHeader
          className={thSeparated}
          hasConsecutiveRooms={hasConsecutiveRooms}
          hasHiddenStock={hasHiddenStock}
          isCabin={isCabin}
          numberOfAdults={counters.adults}
          numberOfBabies={counters.babies}
          numberOfChildren={counters.children}
          numberOfRooms={counters.rooms}
          stock={stock}
          theme={theme}
        />
      )}
      {hasFlexRates && proposal && selectedRate && setSelectedRate && (
        <FlexRates
          className={thFlexRates}
          isInMoreRoomLayer={isInMoreRoomLayer}
          isLoading={isLoading}
          isMobile={isMobile}
          proposal={proposal}
          selectedRate={selectedRate}
          setSelectedRate={setSelectedRate}
          switchOffers={switchOffers}
          theme={theme}
        />
      )}
      {rooms.map((room, i) => (
        <Fragment key={room.id}>
          {i > 0 && <AccommodationSeparator />}
          <AccommodationRoom
            {...asWidgetProps<ComponentProps<typeof AccommodationRoom>>(
              room as ComponentProps<typeof AccommodationRoom>,
            )}
            accommodationsComparator={accommodationsComparator}
            className={thRoom}
            hasDates={hasConsecutiveRooms}
            hasHiddenStock={hasHiddenStock}
            isBooking={isBooking}
            isInMoreRoomLayer={isInMoreRoomLayer}
            isMobile={isMobile}
            proposal={proposal}
            stock={counters && counters.rooms > 1 && stock > 0 ? REMAINING_STOCK_LIMIT + 1 : stock}
            theme={theme}
          />
        </Fragment>
      ))}
    </div>
  );
};
